import type { EnergyLocation, EnergyTariff } from 'src/types/EnergyTariff';
import { get } from 'svelte/store';
import { home_data, profile_data } from 'src/stores/account/user';
import { delete_home } from 'src/helpers/home_helpers';
import { delete_profile, save_profile } from 'src/helpers/profile_helpers';
import { buildVerivoxRedirectUrl } from './verivoxRedirect';

const VERIVOX_BASE_URL = 'https://verivox-proxy.shelly.cloud';
const OLD_STORAGE_KEY = 'shelly_energy_location';
const LEGACY_HOME_DATA_KEY = 'energyLocation'; // Old key for migration
const DE_USERS_KEY = 'de_users';
const VERIVOX_INTEGRATION_KEY = 'verivox_integration';

// Verivox API Configuration
const VERIVOX_PARTNER_ID = '5762'; // Default, should be configurable
const VERIVOX_SUB_PARTNER_ID = '123'; // Default
const VERIVOX_CAMPAIGN_ID = '2'; // Default
const VERIVOX_PROFILE = 'h0'; // Default profile

// API Version Headers
const ENERGY_LOCATION_VERSION = 'application/vnd.verivox.energyLocation-v3+json';
const ELECTRICITY_CRITERIA_VERSION = 'application/vnd.verivox.electricityCriteria-v6+json';
const ELECTRICITY_OFFER_VERSION = 'application/vnd.verivox.electricityOffer-v10+json';

export interface EnergyLocationData {
	postalCode: string;
	locationId: string;
	locationString?: string;
	dismissed?: boolean;
	dismissalDate?: number;
	annualConsumption?: number;
	consumptionPreference?: 'actual' | 'average' | 'custom';
	dataConfirmationAccepted?: boolean;
}

// Verivox API Response Types
export interface VerivoxLocationResponse {
	postCode: string;
	mainName: string;
	productType: string;
	totalResults: string;
	locationList: Array<{
		id: string;
		fullName: string;
		mainName: string;
		partName?: string;
	}>;
}

export interface LocationsResponse {
	isok: boolean;
	data?: {
		postCode: string;
		mainName: string;
		locations: EnergyLocation[];
	};
	error?: string;
}

// Raw Verivox API offer structure (simplified)
export interface VerivoxOffer {
	tariff?: {
		id?: string;
		content?: {
			text?: string;
			longName?: string;
		};
		longName?: string;
	};
	provider?: {
		content?: {
			text?: string;
			longName?: string;
		};
		longName?: string;
		logoUrl?: string;
	};
	cost?: {
		totalCost?: {
			amount?: string | number;
		};
		unitPrice?: {
			amount?: string | number;
		};
		fixedCosts?: {
			amount?: string | number;
		};
	};
	signup?: {
		responsive?: {
			url?: string;
		};
		desktop?: {
			url?: string;
		};
	};
	[key: string]: any; // Allow other properties from Verivox API
}

// Advertisement offer wrapper structure
export interface VerivoxAdvertisementOffer {
	type?: string;
	rank?: string;
	offer: VerivoxOffer;
	[key: string]: any; // Allow other properties
}

export interface TariffsResponse {
	isok: boolean;
	data?: {
		offers?: VerivoxOffer[];
		advertisementOffers?: VerivoxAdvertisementOffer[];
		benchmarkTariff?: VerivoxOffer;
		[key: string]: any; // Allow other properties from Verivox API
	};
	error?: string;
}

export const energyPriceApi = {
	buildVerivoxRedirectUrl,

	getCachedLocation(): EnergyLocationData | null {
		try {
			const profileData = get(profile_data);
			if (!profileData || typeof profileData !== 'object') {
				return null;
			}

			// Check new nested structure: de_users.verivox_integration
			if (profileData[DE_USERS_KEY]?.[VERIVOX_INTEGRATION_KEY]) {
				return profileData[DE_USERS_KEY][VERIVOX_INTEGRATION_KEY];
			}

			// Migrate from legacy home_data key (energyLocation)
			const homeData = get(home_data);
			if (homeData[LEGACY_HOME_DATA_KEY]) {
				const legacyData = homeData[LEGACY_HOME_DATA_KEY];
				// Migrate to new structure (async, but we don't wait for it)
				this.saveLocation(
					legacyData.postalCode,
					legacyData.locationId,
					legacyData.locationString,
					legacyData.annualConsumption,
					legacyData.consumptionPreference,
					legacyData.dataConfirmationAccepted,
				).catch((err) => {
					console.error('Failed to migrate legacy energy location data:', err);
				});
				// Delete legacy key
				delete_home(LEGACY_HOME_DATA_KEY).catch((err) => {
					console.error('Failed to delete legacy energy location key:', err);
				});
				return legacyData;
			}

			// Migrate from old localStorage key if it exists
			const oldData = localStorage.getItem(OLD_STORAGE_KEY);
			if (oldData) {
				try {
					const migratedData = JSON.parse(oldData);
					// Save to new structure and remove old key (async, but we don't wait for it)
					this.saveLocation(
						migratedData.postalCode,
						migratedData.locationId,
						migratedData.locationString,
						migratedData.annualConsumption,
						migratedData.consumptionPreference,
						migratedData.dataConfirmationAccepted,
					).catch((err) => {
						console.error('Failed to migrate old energy location data:', err);
					});
					localStorage.removeItem(OLD_STORAGE_KEY);
					return migratedData;
				} catch (err) {
					console.error('Failed to migrate old energy location data:', err);
					localStorage.removeItem(OLD_STORAGE_KEY);
				}
			}
		} catch (err) {
			console.error('Failed to read energy location from profile_data:', err);
		}
		return null;
	},

	async saveLocation(
		postalCode: string,
		locationId: string,
		locationString?: string,
		annualConsumption?: number,
		consumptionPreference?: 'actual' | 'average' | 'custom',
		dataConfirmationAccepted?: boolean,
	): Promise<void> {
		try {
			const existingData = this.getCachedLocation();
			const locationData: EnergyLocationData = {
				postalCode: postalCode.trim(),
				locationId,
				locationString,
				annualConsumption: annualConsumption ?? existingData?.annualConsumption,
				consumptionPreference: consumptionPreference ?? existingData?.consumptionPreference,
				dismissed: existingData?.dismissed,
				dismissalDate: existingData?.dismissalDate,
				dataConfirmationAccepted:
					dataConfirmationAccepted !== undefined
						? dataConfirmationAccepted
						: existingData?.dataConfirmationAccepted,
			};

			// Get existing de_users object or create new one
			const profileData = get(profile_data);
			const existingDeUsers = profileData?.[DE_USERS_KEY] || {};

			// Update verivox_integration within de_users object
			const updatedDeUsers = {
				...existingDeUsers,
				[VERIVOX_INTEGRATION_KEY]: locationData,
			};

			// Save entire de_users object
			await save_profile(DE_USERS_KEY, updatedDeUsers);

			// Remove old storage keys if they exist
			if (localStorage.getItem(OLD_STORAGE_KEY)) {
				localStorage.removeItem(OLD_STORAGE_KEY);
			}
			// Note: Legacy home_data key (energyLocation) will be cleaned up on next read via migration
		} catch (err) {
			console.error('Failed to save energy location to profile_data:', err);
		}
	},

	getCachedConsumption(): number | null {
		const locationData = this.getCachedLocation();
		if (!locationData) return null;

		// Return consumption based on preference
		if (locationData.consumptionPreference === 'average') {
			return 2500;
		}
		// Both 'actual' and 'custom' use annualConsumption
		return locationData.annualConsumption ?? null;
	},

	async clearCachedLocation(): Promise<void> {
		try {
			const profileData = get(profile_data);
			const existingDeUsers = profileData?.[DE_USERS_KEY];

			if (existingDeUsers && VERIVOX_INTEGRATION_KEY in existingDeUsers) {
				// Remove verivox_integration from de_users object
				const updatedDeUsers = { ...existingDeUsers };
				delete updatedDeUsers[VERIVOX_INTEGRATION_KEY];

				// If de_users object is now empty, delete the entire key
				if (Object.keys(updatedDeUsers).length === 0) {
					await delete_profile(DE_USERS_KEY);
				} else {
					// Otherwise, save the updated de_users object
					await save_profile(DE_USERS_KEY, updatedDeUsers);
				}
			}

			// Also clean up legacy keys
			if (localStorage.getItem(OLD_STORAGE_KEY)) {
				localStorage.removeItem(OLD_STORAGE_KEY);
			}
			// Clean up legacy home_data key if it exists
			const homeData = get(home_data);
			if (homeData?.[LEGACY_HOME_DATA_KEY]) {
				await delete_home(LEGACY_HOME_DATA_KEY).catch(() => {
					// Ignore errors if key doesn't exist
				});
			}
		} catch (err) {
			console.error('Failed to clear energy location from profile_data:', err);
		}
	},

	async fetchLocations(postalCode: string): Promise<LocationsResponse> {
		// Direct Verivox API call
		try {
			const url = `${VERIVOX_BASE_URL}/servicehook/locations/electricity/postCode/${encodeURIComponent(
				postalCode.trim(),
			)}/?partnerId=${VERIVOX_PARTNER_ID}`;

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					Accept: ENERGY_LOCATION_VERSION,
				},
			});

			if (!response.ok) {
				return {
					isok: false,
					error: `HTTP ${response.status}: ${response.statusText}`,
				};
			}

			const verivoxData: VerivoxLocationResponse = await response.json();

			// Transform Verivox response to expected format
			return {
				isok: true,
				data: {
					postCode: verivoxData.postCode,
					mainName: verivoxData.mainName,
					locations: verivoxData.locationList.map((loc) => ({
						id: loc.id,
						fullName: loc.fullName,
						mainName: loc.mainName,
						partName: loc.partName,
					})),
				},
			};
		} catch (error) {
			console.error('Failed to fetch locations from Verivox API:', error);
			return {
				isok: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			};
		}
	},

	async fetchTariffs(
		postalCode: string,
		locationId: string,
		benchmarkTariffId?: number | null,
	): Promise<TariffsResponse> {
		// Direct Verivox API call
		try {
			// Use benchmarkPermanentTariffId if provided, otherwise 0 (basic tariff)
			const benchmarkPermanentTariffId =
				benchmarkTariffId !== undefined && benchmarkTariffId !== null ? benchmarkTariffId : 0;

			// Get consumption from cache based on preference, fallback to default
			const cachedConsumption = this.getCachedConsumption();
			let annualTotal = cachedConsumption !== null ? cachedConsumption : 2500;
			// Convert to integer
			annualTotal = Number(annualTotal.toFixed(0));

			// Build request body according to Verivox API specification
			const requestBody = {
				profile: VERIVOX_PROFILE,
				prepayment: false,
				signupOnly: true,
				includePackageTariffs: false,
				includeTariffsWithDeposit: false,
				includeNonCompliantTariffs: false,
				bonusIncluded: 'compliant' as const,
				maxResultsPerPage: 20,
				onlyProductsWithGoodCustomerRating: false,
				benchmarkTariffId: 0,
				benchmarkPermanentTariffId,
				benchmarkUnitPrice: null,
				benchmarkFixedCost: null,
				benchmarkProviderId: null,
				paolaLocationId: locationId === '0' ? undefined : locationId,
				includeEcoTariffs: {
					includesNonEcoTariffs: true,
				},
				ecoProductType: 0,
				maxContractDuration: 12,
				maxContractProlongation: 12,
				usage: {
					annualTotal,
					offPeakUsage: 0,
				},
				priceGuarantee: {
					minDurationInMonths: 1000,
				},
				maxTariffsPerProvider: 2,
				cancellationPeriod: {
					unit: 'month' as const,
					amount: 1,
				},
				previewDisplayTime: null,
				onlyRegionalTariffs: false,
				sorting: {
					criterion: 'TotalCosts' as const,
					direction: 'Ascending' as const,
				},
				includeSpecialBonusesInCalculation: 'None' as const,
				totalCostViewMode: 1,
				qualifiedForCashbackOnly: false,
				minContractDuration: null,
				dynamicTariffs: false,
			};

			// Remove paolaLocationId if it's undefined (for postcodes without locations)
			if (requestBody.paolaLocationId === undefined) {
				delete requestBody.paolaLocationId;
			}

			const url = `${VERIVOX_BASE_URL}/servicehook/offers/electricity/postCode/${encodeURIComponent(
				postalCode.trim(),
			)}/custom?partnerId=${VERIVOX_PARTNER_ID}&subPartnerId=${VERIVOX_SUB_PARTNER_ID}&campaignId=${VERIVOX_CAMPAIGN_ID}`;

			// POST request (Verivox API returns redirect to page 1, which fetch follows automatically)
			// According to Verivox docs: POST sends criteria, then redirects to GET for page 1 results
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': ELECTRICITY_CRITERIA_VERSION,
					Accept: ELECTRICITY_OFFER_VERSION,
				},
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				return {
					isok: false,
					error: `HTTP ${response.status}: ${response.statusText}`,
				};
			}

			const verivoxData = await response.json();

			// Transform Verivox response to expected format
			return {
				isok: true,
				data: {
					offers: verivoxData.offers || [],
					advertisementOffers: verivoxData.advertisementOffers || [],
					benchmarkTariff: verivoxData.benchmarkTariff || null,
				},
			};
		} catch (error) {
			console.error('Failed to fetch tariffs from Verivox API:', error);
			return {
				isok: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			};
		}
	},
};
